import React, { Fragment, useEffect, useState } from "react";
import { Typography, Snackbar, Tooltip, Button, IconButton } from "@mui/material";
import "./QrHistory.css";
import CustomTable from "../components/CustomTable";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { GridColDef } from "@mui/x-data-grid";
import { getQrHistory, downloadQrFile, getS3FileAsBuffer } from "../services/apiService";

interface DataItem {
  [key: string]: any;
}

export type ColumnConfig = GridColDef<DataItem>;

const QrHistory = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [paginationData, setPaginationData] = useState({
    page: 0,
    pageSize: 10,
  });
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
  });

  // Fetch data on mount and on pagination change
  useEffect(() => {
    fetchData(paginationData);
  }, [paginationData]);

  const fetchData = async (pageData: any) => {
    setLoading(true);
    try {
      const response = await getQrHistory();

      if (response?.data?.code === 200 && response?.data?.qrHistory) {
        const qrHistoryData = response?.data?.qrHistory;
        const finalData = qrHistoryData?.map((item: any, index: number) => ({
          ...item,
          sno: index + 1,
          purchaseOrderNumber: item?.purchaseOrder, // Map field name
        }));
        setData(finalData);
        setTotalRows(qrHistoryData?.length);
      } else {
        setData([]);
        toast.error(response?.data?.message || "Failed to fetch QR history data!");
      }
    } catch (error) {
      console.error("Error fetching QR history data:", error);
      toast.error("Something went wrong, Please try again");
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (pageData: any) => {
    setPaginationData({
      page: pageData.page,
      pageSize: pageData.pageSize,
    });
  };
  const downloadFile = async (batchId: number, fileUrl: string) => {
    if (!fileUrl) {
      toast.error("No file available for download");
      return;
    }

    try {
      const response = await downloadQrFile(batchId);
      if (response?.code === 200 && response?.fileUrl) {
        // const link = document.createElement("a");
        // link.href = response?.fileUrl;
        // link.setAttribute("download", `qr_batch_${batchId}.pdf`);
        // document.body.appendChild(link);
        // link.click();
        // document.body.removeChild(link);

        window.open(response?.fileUrl, '_blank');

        toast.success("File opened in new tab!");
      } else {
        toast.warning(response?.message || "Internal server error, please try again later.");
      }
    } catch (error) {
      toast.error("Failed to download file, please try again later.");
    }
  };

  const columns: ColumnConfig[] = [
    // { field: "sno", headerName: "S.No", width: 100, align: "center", headerAlign: "center" },
    { field: "batchId", headerName: "Batch ID", width: 150, align: "center", headerAlign: "center" },
    { field: "skuCode", headerName: "SKU Code", width: 150, align: "center", headerAlign: "center" },
    { field: "purchaseOrder", headerName: "Purchase Order", width: 180, align: "center", headerAlign: "center" },
    { field: "quantity", headerName: "Quantity", width: 120, align: "center", headerAlign: "center" },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        const hasFile = !!params.row.fileUrl;
        const tooltipText = hasFile
          ? "Download File"
          : "File is In-Progress";
        const buttonClass = hasFile
          ? "download-button"
          : "download-button-incomplete";

        return (
          <Tooltip title={tooltipText} placement="bottom">
            <Button
              className={buttonClass}
              onClick={() => downloadFile(params.row.batchId, params.row.fileUrl)}
              disabled={!hasFile}
              endIcon={<ArrowForwardIcon className="icon-alignment" />}
            >
              {
                hasFile ? "Download" : "In-Progress"
              }
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  const closeSnackBar = () => {
    setSnackbarState((prev) => ({ ...prev, open: false, message: "" }));
  };

  const action = (
    <Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={closeSnackBar}
      >
        <CloseIcon fontSize="small" color="warning" />
      </IconButton>
    </Fragment>
  );

  return (
    <div>
      <div className="qr-history-screen">

        <div className="qr-history-header">
          <Typography variant="h5" className="qr-history-title">
            QR History
          </Typography>
          <Typography variant="body2" className="qr-history-subtitle">
            View and download your QR generation history
          </Typography>
        </div>

        <div className="qr-history-table">
          <CustomTable
            editMode={"row"}
            data={data}
            columns={columns}
            loading={loading}
            getRowId={(row: any) => row.batchId}
          />
        </div>

        <Snackbar
          anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
          onClose={closeSnackBar}
          open={snackbarState.open}
          message={snackbarState.message}
          action={action}
          autoHideDuration={3000}
        />
      </div>
    </div>
  );
};

export default QrHistory;